import { useState } from "react";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import { useDocsetManifest } from "../../context/DocsetContext";
import { useSearch } from "../../context/SearchContext";
import { docsetPath } from "../../utils/basePath";
import type { SearchStatus, SearchResult } from "../../types/search";
import { MagnifyingGlassIcon } from "../icons/MagnifyingGlass";
import { MenuIcon } from "../icons/Menu";
import { CloseIcon } from "../icons/Close";

type HeaderBarProps = {
  accentColor: string;
  isSidebarOpen: boolean;
  onToggleSidebar: () => void;
  onNavigate: () => void;
  activeTitle: string;
};

export function HeaderBar({
  accentColor,
  isSidebarOpen,
  onToggleSidebar,
  onNavigate,
  activeTitle
}: HeaderBarProps) {
  const manifest = useDocsetManifest();
  const navigate = useNavigate();
  const { status, query, setQuery, results, ensureReady, error } = useSearch();
  const [isFocused, setFocused] = useState(false);

  const topResult = results[0];

  const handleNavigate = (path: string) => {
    const [pagePath, hash] = path.split("#");
    navigate({
      pathname: docsetPath(manifest.id, pagePath),
      hash: hash ? `#${hash}` : undefined
    });
    setQuery("");
    onNavigate();
    setFocused(false);
  };

  const showDropdown = isFocused && (query.trim().length > 0 || status === "loading");

  return (
    <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-slate-200 bg-white px-4 shadow-sm lg:px-6">
      <div className="flex items-center gap-3 pr-4">
        <button
          type="button"
          className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white p-2 text-slate-700 shadow-sm transition hover:bg-slate-50 lg:hidden"
          onClick={onToggleSidebar}
          aria-label={isSidebarOpen ? "Close navigation" : "Open navigation"}
        >
          {isSidebarOpen ? <CloseIcon className="h-4 w-4" /> : <MenuIcon className="h-4 w-4" />}
        </button>
        <h1 className="truncate text-base font-semibold tracking-tight text-slate-900">
          {activeTitle || manifest.title}
        </h1>
      </div>
      <div className="relative flex-1 min-w-0 max-w-md">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onFocus={() => {
            setFocused(true);
            ensureReady().catch(() => undefined);
          }}
          onBlur={() => {
            window.setTimeout(() => setFocused(false), 200);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" && topResult) {
              event.preventDefault();
              handleNavigate(topResult.path);
            } else if (event.key === "Escape") {
              setFocused(false);
            }
          }}
          placeholder="Search documentation…"
          className={clsx(
            "w-full rounded-lg border bg-white py-2 pl-10 pr-3 text-sm text-slate-900 placeholder-slate-400 transition focus:outline-none",
            status === "error"
              ? "border-red-500 focus:ring-2 focus:ring-red-400/40"
              : "border-slate-300"
          )}
          style={
            status === "error"
              ? undefined
              : isFocused
                ? {
                    borderColor: accentColor,
                    boxShadow: `0 0 0 2px ${accentColor}33`
                  }
                : undefined
          }
        />
        <SearchDropdown
          visible={showDropdown}
          status={status}
          results={results}
          error={error}
          onSelect={handleNavigate}
          accentColor={accentColor}
        />
      </div>
    </header>
  );
}

type SearchDropdownProps = {
  visible: boolean;
  status: SearchStatus;
  results: SearchResult[];
  error: Error | null;
  onSelect: (path: string) => void;
  accentColor: string;
};

function SearchDropdown({
  visible,
  status,
  results,
  error,
  onSelect,
  accentColor
}: SearchDropdownProps) {
  if (!visible) {
    return null;
  }

  const stateMessage =
    status === "loading"
      ? "Loading search index…"
      : status === "ready" && results.length === 0
        ? "No matches found."
        : error
          ? error.message
          : null;

  return (
    <div className="absolute right-0 top-12 z-30 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg shadow-slate-900/5">
      <div className="max-h-80 overflow-y-auto py-2">
        {stateMessage ? (
          <div
            className={clsx(
              "px-4 py-3 text-sm",
              status === "error" ? "text-red-600" : "text-slate-600"
            )}
          >
            {stateMessage}
          </div>
        ) : null}
        {status === "ready"
          ? results.map((result, index) => {
              const label = result.headingTitle
                ? `${result.pageTitle} › ${result.headingTitle}`
                : result.pageTitle;
              return (
                <button
                  key={result.path}
                  onClick={() => onSelect(result.path)}
                  className="block w-full px-4 py-2 text-left text-sm text-slate-800 transition hover:bg-slate-50"
                  style={index === 0 ? { backgroundColor: `${accentColor}0D` } : undefined}
                >
                  <span>{label}</span>
                  {result.headingTitle ? (
                    <p className="mt-1 text-xs text-slate-500">{result.pageTitle}</p>
                  ) : null}
                </button>
              );
            })
          : null}
      </div>
      <div className="border-t border-slate-200 px-4 py-2 text-[11px] uppercase tracking-wide text-slate-500">
        Enter ↵ to open • Esc to dismiss
      </div>
    </div>
  );
}
